import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { useCan } from '@/hooks/useCan'
import { enrolmentApi, enrolmentKeys, type EnrolmentCode } from '../api'

/**
 * On the Users screen, for someone who may approve: the enrolment codes waiting for a second
 * administrator, each opening that user's codes. Nothing is shown when nothing is waiting, and
 * nothing is asked of the server by someone who could not approve anyway.
 */
export function PendingEnrolmentApprovals({ onOpen }: { onOpen: (user: { id: number; name?: string; email: string }) => void }) {
    const { t } = useTranslation()
    const mayApprove = useCan('users.enrolment.approve')
    const { data = [] } = useQuery({ queryKey: enrolmentKeys.pending, queryFn: enrolmentApi.pending, enabled: mayApprove, refetchInterval: 60_000 })
    const waiting = data.filter((code): code is EnrolmentCode & { user: NonNullable<EnrolmentCode['user']> } => code.user !== null)
    if (!mayApprove || waiting.length === 0) return null
    return (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm" data-testid="pending-enrolment-approvals" role="status">
            <p className="font-medium">{t('enrolment.pending.title', { count: waiting.length })}</p>
            <ul className="mt-2 space-y-1">
                {waiting.map((code) => (
                    <li key={code.id} className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate">
                            {code.user.name || code.user.email}
                            <span className="text-muted-foreground"> · {t('enrolment.admin.issuedBy', { name: code.issuedByUser?.name || code.issuedByUser?.email || '' })}</span>
                        </span>
                        <Button size="sm" variant="outline" onClick={() => onOpen({ id: code.user.id, name: code.user.name ?? undefined, email: code.user.email })}>
                            {t('enrolment.pending.review')}
                        </Button>
                    </li>
                ))}
            </ul>
        </div>
    )
}
